/**
 * ComponentLayoutEditor - SoW-311
 * Visual editor for component layout configuration
 * Features:
 * - Live preview of component appearance
 * - 9-point grid for icon positioning
 * - Icon size selector
 * - Text position and alignment controls
 * - Clone template save/apply functionality
 */

import React, { useState, useCallback } from 'react';
import type {
  ComponentLayoutConfig,
  IconSize,
  IconPosition,
  TextPosition,
  TextAlignment,
  TextFontSize,
} from '@fmeastudio/shared';
import {
  DEFAULT_LAYOUT_CONFIG,
  ICON_SIZE_PX,
  TEXT_FONT_SIZE_PX,
  ICON_POSITIONS,
  ICON_SIZES,
  TEXT_POSITIONS,
  TEXT_ALIGNMENTS,
  TEXT_FONT_SIZES,
} from '@fmeastudio/shared';
import {
  useLayoutTemplates,
  useCreateLayoutTemplate,
  useDeleteLayoutTemplate,
} from '@/hooks/useLayoutTemplates';

export interface ComponentLayoutEditorProps {
  /** Current layout configuration */
  layoutConfig?: ComponentLayoutConfig;
  /** Callback when layout changes */
  onChange: (layoutConfig: ComponentLayoutConfig) => void;
  /** Component name for preview */
  componentName?: string;
  /** Thumbnail URL for preview */
  thumbnailUrl?: string;
  /** Component type for styling */
  componentType?: 'SYSTEM' | 'SUBSYSTEM' | 'COMPONENT' | 'PART';
  /** Actual diagram box width (for accurate preview) */
  diagramWidth?: number | null;
  /** Actual diagram box height (for accurate preview) */
  diagramHeight?: number | null;
}

// Component type colors (matching ComponentBlock.tsx)
const COMPONENT_TYPE_COLORS = {
  SYSTEM: { bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
  SUBSYSTEM: { bg: '#f0fdf4', border: '#22c55e', text: '#166534' },
  COMPONENT: { bg: '#fefce8', border: '#eab308', text: '#854d0e' },
  PART: { bg: '#faf5ff', border: '#a855f7', text: '#6b21a8' },
};

// Position labels for UI
const POSITION_LABELS: Record<IconPosition, string> = {
  'top-left': 'Top Left',
  'top-center': 'Top Center',
  'top-right': 'Top Right',
  'center-left': 'Center Left',
  'center': 'Center',
  'center-right': 'Center Right',
  'bottom-left': 'Bottom Left',
  'bottom-center': 'Bottom Center',
  'bottom-right': 'Bottom Right',
};

const TEXT_POSITION_LABELS: Record<TextPosition, string> = {
  top: 'Above Box',
  bottom: 'Below Box',
  'inside-top': 'Inside Top',
  'inside-bottom': 'Inside Bottom',
  hidden: 'Hidden',
};

const SIZE_LABELS: Record<IconSize, string> = {
  xs: 'XS',
  sm: 'SM',
  md: 'MD',
  lg: 'LG',
  xl: 'XL',
  xxl: '2XL',
  '3xl': '3XL',
  '4xl': '4XL',
  '5xl': '5XL',
};

const TEXT_SIZE_LABELS: Record<TextFontSize, string> = {
  xs: 'XS',
  sm: 'SM',
  md: 'MD',
  lg: 'LG',
  xl: 'XL',
  '2xl': '2XL',
  '3xl': '3XL',
  '4xl': '4XL',
  '5xl': '5XL',
};

// Default box dimensions (from ComponentBlock.tsx)
const DEFAULT_BOX_WIDTH = 140;
const DEFAULT_BOX_HEIGHT = 100;

export const ComponentLayoutEditor: React.FC<ComponentLayoutEditorProps> = ({
  layoutConfig,
  onChange,
  componentName = 'Component Name',
  thumbnailUrl,
  componentType = 'COMPONENT',
  diagramWidth,
  diagramHeight,
}) => {
  const config = layoutConfig || DEFAULT_LAYOUT_CONFIG;
  const colors = COMPONENT_TYPE_COLORS[componentType];

  // Use actual dimensions (1:1 preview) - no scaling
  // This ensures the preview matches exactly what will appear on the canvas
  const previewWidth = diagramWidth ?? DEFAULT_BOX_WIDTH;
  const previewHeight = diagramHeight ?? DEFAULT_BOX_HEIGHT;

  // Clone template state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Clone template hooks
  const { data: templates = [], isLoading: templatesLoading } = useLayoutTemplates();
  const createTemplate = useCreateLayoutTemplate();
  const deleteTemplate = useDeleteLayoutTemplate();

  // Handle applying a template
  const handleApplyTemplate = useCallback(
    (templateId: string) => {
      const template = templates.find((t) => t.id === templateId);
      if (template?.layoutConfig) {
        onChange(template.layoutConfig as ComponentLayoutConfig);
        setSelectedTemplateId('');
      }
    },
    [templates, onChange]
  );

  // Handle saving current layout as template
  const handleSaveTemplate = useCallback(async () => {
    if (!newTemplateName.trim()) return;

    try {
      await createTemplate.mutateAsync({
        name: newTemplateName.trim(),
        layoutConfig: config,
      });
      setNewTemplateName('');
      setShowSaveDialog(false);
    } catch (error) {
      // Error is handled by the hook
    }
  }, [newTemplateName, config, createTemplate]);

  // Handle deleting a template
  const handleDeleteTemplate = useCallback(
    async (templateId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (window.confirm('Delete this layout template?')) {
        await deleteTemplate.mutateAsync(templateId);
      }
    },
    [deleteTemplate]
  );

  // Update icon config
  const updateIconConfig = useCallback(
    (updates: Partial<ComponentLayoutConfig['icon']>) => {
      onChange({
        ...config,
        icon: { ...config.icon, ...updates },
      });
    },
    [config, onChange]
  );

  // Update text config
  const updateTextConfig = useCallback(
    (updates: Partial<ComponentLayoutConfig['text']>) => {
      onChange({
        ...config,
        text: { ...config.text, ...updates },
      });
    },
    [config, onChange]
  );

  // Reset to defaults
  const handleReset = useCallback(() => {
    onChange(DEFAULT_LAYOUT_CONFIG);
  }, [onChange]);

  const iconSize = ICON_SIZE_PX[config.icon.size];
  const textFontSize = TEXT_FONT_SIZE_PX[config.text.fontSize];

  // Get icon position styles for preview
  const getIconPositionStyles = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms ease',
    };

    switch (config.icon.position) {
      case 'top-left':
        return { ...baseStyle, top: 8, left: 8 };
      case 'top-center':
        return { ...baseStyle, top: 8, left: '50%', transform: 'translateX(-50%)' };
      case 'top-right':
        return { ...baseStyle, top: 8, right: 8 };
      case 'center-left':
        return { ...baseStyle, top: '50%', left: 8, transform: 'translateY(-50%)' };
      case 'center':
        return { ...baseStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      case 'center-right':
        return { ...baseStyle, top: '50%', right: 8, transform: 'translateY(-50%)' };
      case 'bottom-left':
        return { ...baseStyle, bottom: 8, left: 8 };
      case 'bottom-center':
        return { ...baseStyle, bottom: 8, left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-right':
        return { ...baseStyle, bottom: 8, right: 8 };
      default:
        return baseStyle;
    }
  };

  // Get text position styles for preview
  const getTextPositionStyles = (): React.CSSProperties => {
    const alignmentStyle = {
      left: 'flex-start' as const,
      center: 'center' as const,
      right: 'flex-end' as const,
    }[config.text.alignment];

    switch (config.text.position) {
      case 'top':
        return {
          position: 'absolute',
          top: -28,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: alignmentStyle,
        };
      case 'bottom':
        return {
          position: 'absolute',
          bottom: -28,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: alignmentStyle,
        };
      case 'inside-top':
        return {
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          display: 'flex',
          justifyContent: alignmentStyle,
        };
      case 'inside-bottom':
        return {
          position: 'absolute',
          bottom: 8,
          left: 8,
          right: 8,
          display: 'flex',
          justifyContent: alignmentStyle,
        };
      case 'hidden':
        return { display: 'none' };
      default:
        return {};
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Section Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#111827',
          }}
        >
          Layout Configuration
        </label>
        <button
          type="button"
          onClick={handleReset}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: 500,
            color: '#6b7280',
            backgroundColor: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 150ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
        >
          Reset to Default
        </button>
      </div>

      {/* Preview + Controls Container */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {/* Live Preview - 1:1 representation */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            // Add padding to accommodate text positioned outside the box
            padding: '32px 16px',
            minWidth: Math.max(previewWidth + 32, 180),
          }}
        >
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Preview (1:1)</span>
          {/* Show actual dimensions */}
          <span style={{ fontSize: 10, color: '#9ca3af' }}>
            {Math.round(previewWidth)} × {Math.round(previewHeight)}px
          </span>
          <div
            style={{
              position: 'relative',
              width: previewWidth,
              height: previewHeight,
              backgroundColor: colors.bg,
              border: `2px solid ${colors.border}`,
              borderRadius: 12,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              marginTop: 16,
              marginBottom: 16,
              transition: 'all 200ms ease',
            }}
          >
            {/* Thumbnail/Icon */}
            {thumbnailUrl ? (
              <div style={getIconPositionStyles()}>
                <img
                  src={thumbnailUrl}
                  alt=""
                  style={{
                    width: iconSize,
                    height: iconSize,
                    borderRadius: Math.max(4, iconSize / 8),
                    objectFit: 'cover',
                    border: `1px solid ${colors.border}40`,
                  }}
                />
              </div>
            ) : (
              <div style={getIconPositionStyles()}>
                <div
                  style={{
                    width: iconSize,
                    height: iconSize,
                    backgroundColor: `${colors.border}20`,
                    border: `2px dashed ${colors.border}`,
                    borderRadius: Math.max(4, iconSize / 8),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: iconSize * 0.5,
                    color: colors.border,
                  }}
                >
                  ?
                </div>
              </div>
            )}

            {/* Text */}
            {config.text.position !== 'hidden' && (
              <div style={getTextPositionStyles() as React.CSSProperties}>
                <span
                  style={{
                    fontSize: textFontSize,
                    fontWeight: 700,
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: config.text.wrap ? 'normal' : 'nowrap',
                    textAlign: config.text.alignment,
                    maxWidth: '100%',
                  }}
                >
                  {componentName.length > 12 ? componentName.slice(0, 12) + '...' : componentName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Icon Position Grid */}
          <div>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
              Icon Position
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 4,
                width: 'fit-content',
              }}
            >
              {(['top-left', 'top-center', 'top-right', 'center-left', 'center', 'center-right', 'bottom-left', 'bottom-center', 'bottom-right'] as IconPosition[]).map(
                (pos) => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => updateIconConfig({ position: pos })}
                    title={POSITION_LABELS[pos]}
                    style={{
                      width: 32,
                      height: 32,
                      padding: 0,
                      backgroundColor: config.icon.position === pos ? colors.border : '#f3f4f6',
                      border: `2px solid ${config.icon.position === pos ? colors.border : '#e5e7eb'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 150ms',
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        backgroundColor: config.icon.position === pos ? '#ffffff' : colors.border,
                      }}
                    />
                  </button>
                )
              )}
            </div>
          </div>

          {/* Icon Size */}
          <div>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
              Icon Size
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ICON_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => updateIconConfig({ size })}
                  style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 500,
                    color: config.icon.size === size ? '#ffffff' : '#374151',
                    backgroundColor: config.icon.size === size ? colors.border : '#f3f4f6',
                    border: `1px solid ${config.icon.size === size ? colors.border : '#e5e7eb'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                >
                  {SIZE_LABELS[size]}
                </button>
              ))}
            </div>
          </div>

          {/* Text Position */}
          <div>
            <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
              Text Position
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {TEXT_POSITIONS.map((pos) => (
                <button
                  key={pos}
                  type="button"
                  onClick={() => updateTextConfig({ position: pos })}
                  style={{
                    padding: '4px 10px',
                    fontSize: 11,
                    fontWeight: 500,
                    color: config.text.position === pos ? '#ffffff' : '#374151',
                    backgroundColor: config.text.position === pos ? colors.border : '#f3f4f6',
                    border: `1px solid ${config.text.position === pos ? colors.border : '#e5e7eb'}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                >
                  {TEXT_POSITION_LABELS[pos]}
                </button>
              ))}
            </div>
          </div>

          {/* Text Alignment */}
          {config.text.position !== 'hidden' && (
            <div>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                Text Alignment
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                {TEXT_ALIGNMENTS.map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => updateTextConfig({ alignment: align })}
                    style={{
                      padding: '4px 12px',
                      fontSize: 11,
                      fontWeight: 500,
                      color: config.text.alignment === align ? '#ffffff' : '#374151',
                      backgroundColor: config.text.alignment === align ? colors.border : '#f3f4f6',
                      border: `1px solid ${config.text.alignment === align ? colors.border : '#e5e7eb'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      transition: 'all 150ms',
                    }}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Text Font Size */}
          {config.text.position !== 'hidden' && (
            <div>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                Text Size
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                {TEXT_FONT_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => updateTextConfig({ fontSize: size })}
                    style={{
                      padding: '4px 12px',
                      fontSize: 11,
                      fontWeight: 500,
                      color: config.text.fontSize === size ? '#ffffff' : '#374151',
                      backgroundColor: config.text.fontSize === size ? colors.border : '#f3f4f6',
                      border: `1px solid ${config.text.fontSize === size ? colors.border : '#e5e7eb'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      transition: 'all 150ms',
                    }}
                  >
                    {TEXT_SIZE_LABELS[size]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clone Templates Section */}
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>Layout Templates</span>
          <button
            type="button"
            onClick={() => setShowSaveDialog(true)}
            style={{
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 500,
              color: '#ffffff',
              backgroundColor: colors.border,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            Save as Template
          </button>
        </div>

        {/* Save Template Dialog */}
        {showSaveDialog && (
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              backgroundColor: '#f9fafb',
              borderRadius: 8,
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Template name..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  fontSize: 12,
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  outline: 'none',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTemplate();
                  if (e.key === 'Escape') setShowSaveDialog(false);
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim() || createTemplate.isPending}
                style={{
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#ffffff',
                  backgroundColor: newTemplateName.trim() ? colors.border : '#9ca3af',
                  border: 'none',
                  borderRadius: 6,
                  cursor: newTemplateName.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                {createTemplate.isPending ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSaveDialog(false);
                  setNewTemplateName('');
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  color: '#6b7280',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Template List */}
        {templatesLoading ? (
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>Loading templates...</div>
        ) : templates.length === 0 ? (
          <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
            No saved templates yet. Save the current layout to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {templates.map((template) => (
              <div
                key={template.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  cursor: 'pointer',
                  transition: 'all 150ms',
                }}
                onClick={() => handleApplyTemplate(template.id)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#374151' }}>
                  {template.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleDeleteTemplate(template.id, e)}
                  style={{
                    padding: '4px 8px',
                    fontSize: 10,
                    color: '#ef4444',
                    backgroundColor: 'transparent',
                    border: '1px solid #fecaca',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 150ms',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentLayoutEditor;
